import React, { useState, useCallback, useMemo } from "react";
import { usePageTitle } from "../../hooks/usePageTitle";
import { useTargetLedgerData } from "../../hooks/useGenericLedgerData";
import { PpcFarmTargetLedgerData } from "../../types/ppcFarmTypes";
import PpcFarmMetricsBar from "./PpcFarmMetricsBar";
import GenericLedgerControlBar, {
  TARGET_LEDGER_FILTER_CONFIGS,
} from "./GenericLedgerControlBar";
import GenericLedgerDataTable, {
  TARGET_LEDGER_COLUMNS,
} from "./GenericLedgerDataTable";
import GenericLedgerDetailModal from "./GenericLedgerDetailModal";

const PpcFarmTargetLedgerClient: React.FC = () => {
  usePageTitle("Target Ledger | PPC Farm Mail Tracker");

  // Modal state
  const [selectedItem, setSelectedItem] =
    useState<PpcFarmTargetLedgerData | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Use the custom hook for all data management
  const {
    data,
    statistics,
    filterOptions,
    loading,
    searchLoading,
    error,
    pagination,
    filters,
    sortOptions,
    setFilters,
    setSortOptions,
    loadMore,
  } = useTargetLedgerData({
    pageSize: 75,
    initialSort: { field: "dateAdded", direction: "desc" },
  });

  // Prepare filter configurations with actual options
  const filterConfigs = useMemo(() => {
    return TARGET_LEDGER_FILTER_CONFIGS.map((config) => ({
      ...config,
      options: filterOptions[config.key] || [],
    }));
  }, [filterOptions]);

  // Handle sort changes
  const handleSortChange = useCallback(
    (newSort: any) => {
      setSortOptions(newSort);
    },
    [setSortOptions],
  );

  // Handle infinite scroll load more
  const handleLoadMore = useCallback(() => {
    loadMore();
  }, [loadMore]);

  // Handle row click
  const handleRowClick = useCallback((item: PpcFarmTargetLedgerData) => {
    setSelectedItem(item);
    setIsDetailModalOpen(true);
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedItem(null);
  }, []);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Target Ledger
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Track direct mail seller data from the Target Ledger
        </p>
      </div>

      {/* Summary Metrics Bar */}
      <PpcFarmMetricsBar
        statistics={statistics}
        loading={loading && !statistics}
        error={error}
      />

      {/* Control Bar - Reduced spacing */}
      <div className="mt-4">
        <GenericLedgerControlBar
          filters={filters}
          onFiltersChange={setFilters}
          filterConfigs={filterConfigs}
          loading={false}
          totalResults={pagination.totalItems}
          searchPlaceholder="Search by seller name and address..."
        />
      </div>

      {/* Data Table */}
      <GenericLedgerDataTable
        data={data}
        loading={loading}
        error={error}
        sortOptions={sortOptions}
        onSortChange={handleSortChange}
        onRowClick={handleRowClick}
        pagination={pagination}
        onLoadMore={handleLoadMore}
        columns={TARGET_LEDGER_COLUMNS}
        emptyStateMessage="No sellers match your current filters. Try adjusting your search criteria."
      />

      {/* Detail Modal */}
      <GenericLedgerDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        data={selectedItem}
        ledgerType="target"
      />
    </div>
  );
};

export default PpcFarmTargetLedgerClient;
